// License state banner/screen — the SPA half of the remote-license seam
// (backend/shared/licenseClient.js). Polls GET /license/status:
//   blocked → full-screen notice replaces the app (the API is 503ing anyway)
//   grace   → dismissable warning banner (Dev Console is locked; end-user
//             surfaces keep working until the grace window runs out)
//   anything else (dormant/pending/valid, endpoint missing, network error)
//   → renders nothing. Fail-open: this component must never take down an
//   unlicensed-seam app.
import React, { useEffect, useState } from 'react';
import { kitEnv } from '../data/kitEnv.js';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');
const POLL_MS = 10 * 60 * 1000;

export default function LicenseNotice({ children }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/license/status`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setStatus(data);
      } catch { /* fail open */ }
    };
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (status?.state === 'blocked') {
    return (
      <div className="cs-auth-wrap" role="alert" style={{ textAlign: 'center', padding: '4rem 1.5rem' }}>
        <h1 style={{ marginBottom: '0.75rem' }}>License required</h1>
        <p style={{ maxWidth: '42ch', margin: '0 auto', opacity: 0.85 }}>
          This application's license could not be validated and it has been
          paused. Please contact your licensor to restore service.
        </p>
        {status.reason ? (
          <p style={{ marginTop: '1rem', fontSize: '0.85em', opacity: 0.6 }}>{status.reason}</p>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {status?.state === 'grace' && !dismissed && (
        <div
          role="status"
          style={{
            padding: '0.5rem 1rem', textAlign: 'center', fontSize: '0.9em',
            background: 'var(--color-warning-bg, #fff3cd)', color: 'var(--color-warning-text, #664d03)',
          }}
        >
          License validation is failing — this app will pause when the grace
          period ends. Contact your licensor.
          <button
            type="button"
            onClick={() => setDismissed(true)}
            style={{ marginLeft: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', color: 'inherit' }}
          >
            Dismiss
          </button>
        </div>
      )}
      {children}
    </>
  );
}
